import axios from 'axios'
import createAuthRefreshInterceptor from 'axios-auth-refresh'
import { useAuthStore } from '@/stores/auth'

let isRedirectingToLogin = false

/**
 * Redirects user to login page, preserving the current page path for redirect after login
 */
function redirectToLogin() {
  isRedirectingToLogin = true
  window.sessionStorage.setItem('requested_page', window.location.pathname + window.location.search + window.location.hash)
  window.location.href = '/auth/login'
}

// Prefix all axios requests with /api/
const instance = axios.create({
  baseURL: '/api/',
  headers: {
    'X-Requested-With': 'XMLHttpRequest',
  },
})

instance.interceptors.request.use(
  config => {
    // Block all requests if we're redirecting to login
    if (isRedirectingToLogin) {
      return Promise.reject(new Error('Redirecting to login - blocking request'))
    }

    const token = localStorage.getItem('token')

    if (config.headers)
      config.headers.Authorization = token ? `Bearer ${token}` : ''

    return config
  },
)

/**
 * Gets the shadowed student ID from localStorage
 */
function getShadowedStudentId(): number {
  return +(window.localStorage.getItem('shadowed_student_id') || 0)
}

/**
 * Checks for multi-tab shadowing student mismatch
 * Returns true if mismatch detected and handled
 */
function checkShadowingStudentMismatch(authStore: ReturnType<typeof useAuthStore>): boolean {
  if (!authStore.is_shadowing) {
    return false
  }

  const shadowedStudentId = getShadowedStudentId()
  if (!shadowedStudentId) {
    return false
  }

  // If we're shadowing but the student_id in store doesn't match, we have a mismatch
  // This can happen when shadowing different students in multiple tabs
  if (authStore.student_id && authStore.student_id !== shadowedStudentId) {
    // Page reload is simplest - it will reload with correct student from localStorage
    window.location.reload()

    return true
  }

  return false
}

/**
 * Checks for a normal multi-tab login swap.
 * One tab can hold Student 1 in Pinia while another tab replaces localStorage
 * with Student 2's token. A 403 after that means this tab should reboot with
 * the current token instead of continuing with stale student state.
 */
function checkStoredTokenMismatch(authStore: ReturnType<typeof useAuthStore>): boolean {
  const storedToken = localStorage.getItem('token')

  if (!storedToken || !authStore.accessToken || storedToken === authStore.accessToken) {
    return false
  }

  window.location.reload()

  return true
}

// Track ongoing refresh attempts to prevent race conditions
let isRefreshing = false
let refreshPromise: Promise<void> | null = null

interface ExtendedFailedRequest {
  response: { status: number; config: { headers: { Authorization: string | null }; url?: string } }
  _studentBeforeRefresh?: number
}

// Catches 401/403 errors and attempts silent token refresh
async function refreshAuthLogic(failedRequest: ExtendedFailedRequest) {
  const authStore = useAuthStore()
  const status = failedRequest.response.status
  const url = failedRequest.response.config.url || ''

  // Handle 403 - Check for multi-tab shadowing student mismatch
  if (status === 403) {
    // Check if this is due to multi-tab shadowing interference
    if (checkShadowingStudentMismatch(authStore)) {
      return Promise.reject(new Error('Multi-tab shadowing student mismatch detected'))
    }

    if (checkStoredTokenMismatch(authStore)) {
      return Promise.reject(new Error('Multi-tab student login mismatch detected'))
    }

    // If we get here, it's a legitimate 403 (disabled account, no access, etc.)
    // Don't attempt token refresh for 403s - just reject
    return Promise.reject(new Error('Access forbidden'))
  }

  // Skip refresh if this is already a refresh token request to prevent loops
  if (url.includes('/oauth/token-with-session')) {
    // This is a token refresh request itself - refresh token is invalid/expired
    // Silently redirect to login
    redirectToLogin()

    // Return rejected promise to stop a retry
    return Promise.reject(new Error('Token refresh request failed - redirecting to login'))
  }

  // Access token rejected but refresh cookie is gone (logout elsewhere, cookie cleared, etc.)
  // Redirect without calling refreshToken() so we do not throw a Sentry-noisy error.
  if (!authStore.hasRefreshToken) {
    authStore.removeRefreshToken()
    redirectToLogin()

    return Promise.reject(new Error('AUTH_SILENT_LOGIN_REDIRECT'))
  }

  // Handle shadow requests specially
  if (url.includes('/api/myfj-v2-user-shadow')) {
    // For shadow requests, we need to check if student changed after refresh
    // If it did, don't retry - redirect to login to avoid wrong data
    const shadowedStudentId = getShadowedStudentId()
    if (shadowedStudentId) {
      // Store the student for comparison after refresh
      failedRequest._studentBeforeRefresh = shadowedStudentId
    }
  }

  // If already refreshing, wait for that refresh to complete
  if (isRefreshing && refreshPromise) {
    await refreshPromise

    // Token should be refreshed now, axios-auth-refresh will retry the request
    return
  }

  // Mark that we're starting a refresh
  isRefreshing = true
  refreshPromise = (async () => {
    try {
      // Attempt silent token refresh
      await authStore.refreshToken()

      // Verify token was saved
      const token = localStorage.getItem('token')
      if (!token) {
        // No token after refresh - this shouldn't happen, but handle it
        if (!authStore.hasRefreshToken) {
          redirectToLogin()
          throw new Error('AUTH_SILENT_LOGIN_REDIRECT')
        }
        throw new Error('Token refresh failed - no token saved')
      }

      // Check if this was a shadow request and if student changed during refresh
      const studentBeforeRefresh = failedRequest._studentBeforeRefresh
      if (studentBeforeRefresh) {
        const currentShadowedStudentId = getShadowedStudentId()
        if (currentShadowedStudentId !== studentBeforeRefresh) {
          // Student changed during shadow request refresh - don't retry with wrong student
          // Let the error bubble up so router guard can retry loadUserData() with fresh state
          throw new Error('SHADOW_STUDENT_MISMATCH')
        }
      }

      // Token refreshed successfully - axios-auth-refresh will automatically retry the request
      // The request interceptor will pick up the new token from localStorage
    }
    catch (error) {
      const errorMessage = error instanceof Error ? error.message : String(error)
      const isNoTokenSaved = errorMessage.includes('no token saved')
      const isSilentRedirect = errorMessage.includes('AUTH_SILENT_LOGIN_REDIRECT')

      const isTokenRequestFailed = axios.isAxiosError(error)
        && error.config?.url?.includes('token-with-session')

      // "no token saved" indicates refresh succeeded but token wasn't saved - this is a bug
      if (isNoTokenSaved) {
        // eslint-disable-next-line no-console
        console.error('[Axios] refreshAuthLogic - Token refresh succeeded but token not saved', error)
      }

      // refreshToken() already redirected before throwing AUTH_SILENT_LOGIN_REDIRECT
      if (isSilentRedirect) {
        throw error
      }

      // Session ended or refresh rejected — clear cookie and redirect (MyHQ-v3 pattern)
      if (isTokenRequestFailed || !authStore.hasRefreshToken) {
        authStore.removeRefreshToken()
        redirectToLogin()

        throw error
      }
      // Re-throw to prevent retry
      throw error
    }
    finally {
      // Reset the refresh state
      isRefreshing = false
      refreshPromise = null
    }
  })()

  await refreshPromise
}

// 401s handled in refresh auth logic, 404s handled in individual components
// Catch and handle Response Status'
instance.interceptors.response.use(
  response => {
    return response.data
  },
  error => {
    // open snackbar

    return Promise.reject(error)
  },
)

// Instantiate the interceptor
createAuthRefreshInterceptor(instance, refreshAuthLogic, { 'statusCodes': [401, 403] })

export default instance
